import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Text,
  Textarea
} from '@chakra-ui/react'

import { Note_Priorities_Enum, useUpupInsertNoteMutation } from 'graphql/generated'
import axios from 'lib/axios'
import { BooksLockedNotice } from 'lib/buildiumBooksLock'
import { LedgerItem } from 'lib/ledger'
import { useSelector } from 'react-redux'
import { StoreState } from 'store'
import React, { FC, useEffect, useState } from 'react'
import { dates } from '@homevest/utils'
import BuildiumReversalDateNotice from './BuildiumReversalDateNotice'
import useBuildiumReversalDateChoice from './useBuildiumReversalDateChoice'

interface EditLedgerItemProps {
  ledgerItem: LedgerItem
  onClose: () => void
  onSuccess?: () => void | Promise<void>
  isOpen: boolean
  rentalId: string
  blockedEditMessage?: string | null
}

const getEditErrorMessage = (error: unknown, type: LedgerItem['type']) => {
  if (error && typeof error === 'object') {
    const graphQLErrors = 'graphQLErrors' in error ? error.graphQLErrors : undefined

    if (Array.isArray(graphQLErrors)) {
      const firstGraphQLError = graphQLErrors.find(
        (graphQLError) =>
          graphQLError &&
          typeof graphQLError === 'object' &&
          'message' in graphQLError &&
          typeof graphQLError.message === 'string'
      )

      if (firstGraphQLError?.message) {
        return firstGraphQLError.message.replaceAll('[GraphQL] ', '')
      }
    }

    const responseMessage =
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'data' in error.response &&
      error.response.data &&
      typeof error.response.data === 'object' &&
      'message' in error.response.data &&
      typeof error.response.data.message === 'string'
        ? error.response.data.message
        : undefined

    if (responseMessage) {
      return responseMessage
    }

    const directMessage = 'message' in error ? error.message : undefined

    if (typeof directMessage === 'string' && directMessage) {
      return directMessage.replaceAll('[GraphQL] ', '')
    }
  }

  if (error instanceof Error) {
    return error.message
  }

  return `Unable to edit this ${type.toLowerCase()}`
}

export const EditLedgerItemModal: FC<React.PropsWithChildren<EditLedgerItemProps>> = ({
  ledgerItem,
  rentalId,
  isOpen,
  onClose,
  onSuccess,
  blockedEditMessage = null
}) => {
  const [newAmount, setNewAmount] = useState(ledgerItem.amount)
  const [editReason, setEditReason] = useState<string>()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [_n, insertNote] = useUpupInsertNoteMutation()
  const admin = useSelector((state: StoreState) => state.admin)
  const usesBuildiumReversal = ledgerItem.type === 'Charge' || ledgerItem.type === 'Credit'
  const {
    booksLock,
    canReverseOriginal,
    canReverseToday,
    hasBuildiumReversalOption,
    isBooksLockError,
    isBooksLockLoading,
    originalReversalDate,
    resetReversalDateChoice,
    selectedReversalDate,
    setSelectedReversalDate,
    today
  } = useBuildiumReversalDateChoice({
    entryDate: ledgerItem.date,
    isEnabled: usesBuildiumReversal,
    isOpen,
    rentalId
  })
  const ledgerItemLabel = ledgerItem.type === 'Charge' ? 'liability' : 'credit'
  const isAmountInvalid = !Number.isFinite(newAmount) || newAmount <= 0
  const isAmountUnchanged = newAmount === ledgerItem.amount

  useEffect(() => {
    setNewAmount(ledgerItem.amount)
  }, [ledgerItem.amount])

  const handleClose = () => {
    setErrorMessage(null)
    setNewAmount(ledgerItem.amount)
    setEditReason(undefined)
    resetReversalDateChoice()
    onClose()
  }

  const handleSubmit = async () => {
    if (isAmountInvalid || isAmountUnchanged || (usesBuildiumReversal && !selectedReversalDate)) {
      return
    }

    setErrorMessage(null)
    setIsSubmitting(true)

    try {
      if (ledgerItem.type === 'Charge') {
        await axios.put(`/admin/rental_liabilities/${ledgerItem.id}`, {
          amount: newAmount,
          date: ledgerItem.date,
          liability_type_id: ledgerItem.liabilityTypeId,
          note: ledgerItem.note,
          rental_id: rentalId,
          buildium_reversal_date: selectedReversalDate
        })
      } else if (ledgerItem.type === 'Credit') {
        await axios.put(`/admin/rental_credits/${ledgerItem.id}`, {
          amount: newAmount,
          date: ledgerItem.date,
          liability_type_id: ledgerItem.liabilityTypeId,
          note: ledgerItem.note,
          rental_id: rentalId,
          type: ledgerItem.creditType,
          buildium_reversal_date: selectedReversalDate
        })
      }

      let noteText = `Edited ${dates.formatISODate(ledgerItem.date)} ${ledgerItem.liabilityLabel} ${
        ledgerItem.type
      } (${ledgerItem.id}) from $${ledgerItem.amount} -> $${newAmount}`
      if (editReason) {
        noteText += `
        Reason: ${editReason}`
      }

      await insertNote({
        note: noteText,
        category: 'ledger_update',
        created_by_admin_id: admin.id,
        resource_id: rentalId,
        resource_type: 'rentals',
        priority: Note_Priorities_Enum.Default
      })
      handleClose()
      Promise.resolve(onSuccess?.()).catch((error) => {
        console.error('Unable to refresh ledger after editing ledger item', error)
      })
    } catch (error) {
      setErrorMessage(getEditErrorMessage(error, ledgerItem.type))
    } finally {
      setIsSubmitting(false)
    }
  }
  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit {ledgerItem.type}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {blockedEditMessage ? (
            <Alert status='error'>
              <AlertIcon />
              {blockedEditMessage}
            </Alert>
          ) : (
            <>
              <FormControl isInvalid={isAmountInvalid} isDisabled={isSubmitting}>
                <FormLabel>Amount</FormLabel>
                <NumberInput
                  min={0.01}
                  onChange={(_, val) => setNewAmount(val)}
                  precision={2}
                  format={(val) => `$${val}`}
                  step={0.01}
                  value={newAmount}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Current amount: ${ledgerItem.amount.toFixed(2)}</FormHelperText>
                <FormErrorMessage>Enter an amount greater than $0.</FormErrorMessage>
              </FormControl>
              <FormControl isDisabled={isSubmitting}>
                <FormLabel>Reason for edit</FormLabel>
                <Textarea value={editReason} onChange={(evt) => setEditReason(evt.target.value)} />
              </FormControl>
              {usesBuildiumReversal && (
                <FormControl
                  mt={4}
                  isDisabled={isSubmitting || isBooksLockLoading || isBooksLockError}
                >
                  {isBooksLockLoading ? (
                    <Text>Checking Buildium books-lock status...</Text>
                  ) : isBooksLockError || !hasBuildiumReversalOption ? (
                    <BooksLockedNotice lockDate={booksLock?.lockDate} isError={isBooksLockError} />
                  ) : (
                    <BuildiumReversalDateNotice
                      canReverseOriginal={canReverseOriginal}
                      canReverseToday={canReverseToday}
                      ledgerItemLabel={ledgerItemLabel}
                      lockDate={booksLock?.lockDate}
                      originalReversalDate={originalReversalDate}
                      selectedReversalDate={selectedReversalDate}
                      today={today}
                      onChange={setSelectedReversalDate}
                    />
                  )}
                </FormControl>
              )}
              {errorMessage && (
                <Alert status='error' mt={4}>
                  <AlertIcon />
                  {errorMessage}
                </Alert>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {blockedEditMessage ? (
            <Button onClick={handleClose}>Close</Button>
          ) : (
            <Button
              type='submit'
              onClick={handleSubmit}
              isLoading={isSubmitting}
              isDisabled={
                isAmountInvalid ||
                isAmountUnchanged ||
                (usesBuildiumReversal &&
                  (isBooksLockLoading ||
                    isBooksLockError ||
                    !hasBuildiumReversalOption ||
                    !selectedReversalDate))
              }
            >
              Save changes
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
